import { Injectable } from '@angular/core';
import {
  and,
  getDocs,
  getDocsFromServer,
  limit,
  or,
  orderBy,
  Query,
  query,
  Timestamp,
  where,
  writeBatch,
} from '@angular/fire/firestore';
import { Functions, httpsCallable } from '@angular/fire/functions';
import { GetMatchPlayersRequest, GetMatchPlayersResponse, Journey, Match, MatchStatus } from '@models';
import { Season } from '@models';
import { Team } from '@models';
import { DeepPartial, Optional } from '@models';
import { convertTimestampsToDates } from '@utils';
import { add, addDays } from 'date-fns';
import { DocumentData } from 'firebase/firestore';
import {
  combineLatest,
  distinctUntilChanged,
  firstValueFrom,
  map,
  Observable,
  of,
  shareReplay,
  switchMap,
} from 'rxjs';
import { FirestoreService } from './firestore.service';
import { SeasonManagerService } from './season-manager.service';
import { SeasonsService } from './seasons.service';
import { TeamsService } from './team.service';
import { TenantContextService } from './tenant-context.service';

@Injectable({
  providedIn: 'root',
})
export class MatchesService {
  public noRefereeMatches$!: ReturnType<typeof this.buildNoRefereeMatches$>;

  private buildNoRefereeMatches$() {
    return this.seasonsService.all$.pipe(
      distinctUntilChanged(
        (a, b) => a.length === b.length && a.every((s, i) => s.id === b[i].id),
      ),
      switchMap((seasons) => {
        const seasonIds = seasons.map((season) => season.id);
        const chunkArray = (array: string[], size: number) => {
          const result: string[][] = [];
          for (let i = 0; i < array.length; i += size) {
            result.push(array.slice(i, size + i));
          }
          return result;
        };
        const seasonsChunks = chunkArray(seasonIds, 30);
        if (seasonsChunks.length === 0) return of([]);
        return combineLatest(
          seasonsChunks.map((seasonChunk) =>
            this.firestore
              .listDocuments<Match>(
                this.col,
                and(
                  where('status', 'in', [
                    MatchStatus.PENDING,
                    MatchStatus.IN_PROGRESS,
                  ]),
                  where('date', '>=', new Date()),
                  where('date', '<=', add(new Date(), { days: 15 })),
                  where('seasonId', 'in', seasonChunk),
                ),
                orderBy('date', 'asc'),
              )
              .pipe(
                map((matches) =>
                  seasonChunk
                    .map((seasonId) => {
                      const season = seasons.find((s) => s.id === seasonId);
                      if (!season) return null;
                      return {
                        season,
                        matches: matches.filter((m) => m.seasonId === seasonId),
                      };
                    })
                    .filter((sm) => sm !== null),
                ),
              ),
          ),
        ).pipe(
          map((results) => results.flat()),
          map((seasonMatches) =>
            seasonMatches
              .map((sm) => ({
                season: sm!.season,
                matches: sm!.matches.sort((a, b) => {
                  if (!a.date) return 1;
                  if (!b.date) return -1;
                  return a.date.getTime() - b.date.getTime();
                }),
              }))
              .sort((a, b) => a.season.name.localeCompare(b.season.name)),
          ),
        );
      }),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
  }

  public incidentsMatches$!: ReturnType<typeof this.buildIncidentsMatches$>;

  private buildIncidentsMatches$() {
    return this.seasonManager.season$.pipe(
      distinctUntilChanged(),
      switchMap((seasonYear) =>
        this.firestore.listDocuments<Match>(
          this.col,
          where('incident', '!=', null),
          where('seasonYear', '==', seasonYear),
          orderBy('date', 'desc'),
        ),
      ),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
  }

  constructor(
    private functions: Functions,
    private firestore: FirestoreService,
    private teamsService: TeamsService,
    private seasonsService: SeasonsService,
    private seasonManager: SeasonManagerService,
    private tenantContext: TenantContextService,
  ) {
    this.noRefereeMatches$ = this.buildNoRefereeMatches$();
    this.incidentsMatches$ = this.buildIncidentsMatches$();
  }

  private get col() {
    return this.tenantContext.collections.MATCHES;
  }

  get(id: string) {
    return this.firestore.getDocument<Match>(this.col, id);
  }

  getBySeason(seasonId: string) {
    return this.firestore.listDocuments<Match>(
      this.col,
      where('seasonId', '==', seasonId),
    );
  }

  /**
   * One-shot read of every match of a season, **always from the server**.
   *
   * `getBySeason` is a live `collectionData` whose first emission may come from
   * Firestore's in-memory cache with only a subset of the documents (whatever
   * another active listener happened to hold). Screens that decide what to
   * DELETE from that list (`updateTournament`) must use this instead: a partial
   * list means the old matches are never deleted and the calendar ends up
   * duplicated.
   *
   * Rejects when the server cannot be reached (offline): callers must treat
   * that as "cannot know what is stored" and not write anything.
   *
   * Same shape as `listDocuments`: `id` from the document, `deleted` filtered
   * out and timestamps converted to `Date`.
   */
  async getBySeasonFromServer(seasonId: string): Promise<Match[]> {
    const col = this.firestore.getCollectionRef(this.col);
    const q = query<Match, DocumentData>(
      col as unknown as Query<Match>,
      where('seasonId', '==', seasonId),
    );
    const snapshot = await getDocsFromServer(q);
    return snapshot.docs
      .map((d) => ({ ...d.data(), id: d.id }) as Match)
      .filter((m) => m.deleted !== true)
      .map((m) => convertTimestampsToDates(m));
  }

  getFinishedMatchesBySeason(seasonId: string) {
    return this.firestore
      .listDocuments<Match>(
        this.col,
        where('seasonId', '==', seasonId),
        where('status', '==', MatchStatus.FINISHED),
        orderBy('date', 'asc'),
      )
      .pipe(shareReplay(1));
  }

  getFinishedMatchesByTeamAndSeasonId(teamId: string, seasonId: string) {
    return this.firestore.listDocuments<Match>(
      this.col,
      and(
        or(
          where('localTeamId', '==', teamId),
          where('visitorTeamId', '==', teamId),
        ),
        where('seasonId', '==', seasonId),
        where('status', '==', MatchStatus.FINISHED),
      ),
      orderBy('date', 'asc'),
    );
  }

  getByDate(
    startDate: Date,
    endDate: Date,
  ): Observable<(Match & { season: Season })[]> {
    return combineLatest([
      this.firestore.listDocuments<Match>(
        this.col,
        where('date', '>=', Timestamp.fromDate(startDate)),
        where('date', '<=', Timestamp.fromDate(endDate)),
      ),
      this.seasonsService.all$,
    ]).pipe(
      map(([matches, allSeasons]) => {
        const seasonsMap = allSeasons.reduce(
          (acc, s) => {
            acc[s.id] = s;
            return acc;
          },
          {} as Record<string, Season>,
        );
        return matches.map((match) => ({
          ...match,
          season: seasonsMap[match.seasonId],
        }));
      }),
    );
  }

  getMatchesByJourney(teamIds: string[], date: Date) {
    if (teamIds.length === 0) return of([]);
    const startDate = addDays(date, -1);
    const endDate = addDays(date, 1);
    const chunkArray = (arr: string[], size: number) => {
      const result: string[][] = [];
      for (let i = 0; i < arr.length; i += size)
        result.push(arr.slice(i, size + i));
      return result;
    };
    const teamChunks = chunkArray(teamIds, 30);
    return combineLatest(
      teamChunks.map((chunk) =>
        this.firestore.listDocuments<Match>(
          this.col,
          and(
            or(
              where('localTeamId', 'in', chunk),
              where('visitorTeamId', 'in', chunk),
            ),
            where('rest', '==', false),
            where('officialDate', '>=', startDate),
            where('officialDate', '<=', endDate),
          ),
        ),
      ),
    ).pipe(map((results) => results.flat()));
  }

  findMatch(teamId: string, date: Date) {
    return this.teamsService.get(teamId).pipe(
      switchMap((team) => this.teamsService.getByClub(team.clubId!)),
      switchMap((teams) => {
        const teamIds = teams.map((t) => t.id).filter((id) => id !== teamId);
        if (teamIds.length === 0) return of([]);
        const chunkArray = (arr: string[], size: number) => {
          const result: string[][] = [];
          for (let i = 0; i < arr.length; i += size)
            result.push(arr.slice(i, size + i));
          return result;
        };
        const teamChunks = chunkArray(teamIds, 30);
        return combineLatest(
          teamChunks.map((chunk) =>
            this.firestore.listDocuments<Match>(
              this.col,
              where('localTeamId', 'in', chunk),
              where('date', '==', date),
            ),
          ),
        ).pipe(map((results) => results.flat()));
      }),
    );
  }

  numberOfJourneys(seasonId: string) {
    return this.firestore
      .listDocuments<Match>(
        this.col,
        where('seasonId', '==', seasonId),
        orderBy('date', 'desc'),
        limit(1),
      )
      .pipe(map((matches) => matches[0]?.round ?? null));
  }

  create(match: Omit<Match, 'id'>) {
    return this.firestore.create<Match>(this.col, match);
  }

  update(id: string, match: DeepPartial<Match>) {
    return this.firestore.update<Match>(this.col, {
      id,
      ...match,
    } as Partial<Match>);
  }

  createOrUpdate(id: string | undefined, match: Omit<Match, 'id'>) {
    return id ? this.update(id, match) : this.create(match);
  }

  updateMatch(match: Match) {
    return this.firestore.update<Match>(this.col, match);
  }

  async removeAll(seasonId: string) {
    const batch = writeBatch(this.firestore.db);
    const matches = await firstValueFrom(this.getBySeason(seasonId));
    matches.forEach((match) => {
      const ref = this.firestore.getDocumentRef(this.col, match.id);
      batch.delete(ref as any);
    });
    return batch.commit();
  }

  updateTournament(
    actualMatches: Match[],
    newMatches: Optional<Match, 'id'>[],
  ) {
    const batch = writeBatch(this.firestore.db);
    const deleteMatches = actualMatches.filter(
      (m) => !newMatches.find((nm) => nm.id === m.id),
    );
    const updateMatches = newMatches.filter(
      (m) => m.id !== undefined,
    ) as Match[];
    const createMatches = newMatches.filter((m) => !m.id);

    deleteMatches.forEach((match) => {
      const ref = this.firestore.getDocumentRef(this.col, match.id);
      batch.delete(ref as any);
    });
    updateMatches.forEach((match) => {
      const ref = this.firestore.getDocumentRef(this.col, match.id);
      batch.update(ref as any, { ...match });
    });
    createMatches.forEach((match) => {
      const ref = this.firestore.getDocumentRef(
        this.col,
        this.firestore.generateId(this.col),
      );
      batch.set(ref as any, { ...match, id: ref.id });
    });

    return batch.commit();
  }

  getJourneys(seasonId: string) {
    const path = [
      this.tenantContext.collections.SEASONS,
      seasonId,
      this.tenantContext.collections.JOURNEYS,
    ].join('/');
    return this.firestore.listDocuments<Journey>(path);
  }

  updateJourneys(
    seasonId: string,
    journeys: Journey[],
    newJourneys: Optional<Journey, 'id'>[],
  ) {
    const batch = writeBatch(this.firestore.db);
    const path = [
      this.tenantContext.collections.SEASONS,
      seasonId,
      this.tenantContext.collections.JOURNEYS,
    ].join('/');
    const deleteJourneys = journeys.filter(
      (j) => !newJourneys.find((nj) => nj.id === j.id),
    );
    const updateJourneys = newJourneys.filter(
      (j) => j.id !== undefined,
    ) as Journey[];
    const createJourneys = newJourneys.filter((j) => !j.id);

    deleteJourneys.forEach((j) => {
      batch.delete(this.firestore.getDocumentRef(path, j.id) as any);
    });
    updateJourneys.forEach((j) => {
      const { id, ...data } = j;
      batch.update(this.firestore.getDocumentRef(path, id) as any, data);
    });
    createJourneys.forEach((j) => {
      const ref = this.firestore.getDocumentRef(
        path,
        this.firestore.generateId(path),
      );
      batch.set(ref as any, j);
    });

    return batch.commit();
  }

  getMatchesByRound(seasonId: string, round: number) {
    return this.firestore
      .listDocuments<Match>(
        this.col,
        and(where('round', '==', round), where('seasonId', '==', seasonId)),
      )
      .pipe(shareReplay(1));
  }

  getReport() {
    return this.seasonManager.season$.pipe(
      switchMap((seasonYear) =>
        combineLatest([
          this.firestore.listDocuments<Match>(
            this.col,
            where('seasonYear', '==', seasonYear),
          ),
          this.seasonsService.all$,
          this.teamsService.all$,
        ]),
      ),
      map(([matches, allSeasons, allTeams]) => {
        const seasonsMap = allSeasons.reduce(
          (acc, s) => {
            acc[s.id] = s;
            return acc;
          },
          {} as Record<string, Season>,
        );
        const teamsMap = allTeams.reduce(
          (acc, t) => {
            acc[t.id] = t;
            return acc;
          },
          {} as Record<string, Team>,
        );
        return matches.map((match) => ({
          ...match,
          season: seasonsMap[match.seasonId],
          localTeam: match.localTeamId ? teamsMap[match.localTeamId] : null,
          visitorTeam: match.visitorTeamId
            ? teamsMap[match.visitorTeamId]
            : null,
        }));
      }),
      shareReplay(1),
    );
  }

  deleteForever(matchId: string) {
    return this.firestore.deleteForever(this.col, matchId);
  }

  async getMatchesByReferee(refereeId: string) {
    const seasonYear = await firstValueFrom(this.seasonManager.season$);
    const col = this.firestore.getCollectionRef(this.col);
    const q = query<Match, DocumentData>(
      col as unknown as Query<Match>,
      where('refereeId', '==', refereeId),
      where('seasonYear', '==', seasonYear),
    );
    const snapshot = await getDocs(q);
    return snapshot.docs
      .map((d) => ({ ...d.data(), id: d.id }) as Match)
      .filter((m) => !m.deleted)
      .map((m) => convertTimestampsToDates(m))
      .sort((a, b) => a.round - b.round);
  }

  processMatch(
    matchId: string,
    action: 'finish' | 'revert' | 'edit',
    previousMatch?: Match,
  ) {
    const fn = httpsCallable<
      { matchId: string; action: string; previousMatch?: Match },
      { success: boolean }
    >(this.functions, 'processMatch');
    return fn({ matchId, action, previousMatch });
  }

  /**
   * Jugadores de un partido vía el callable `getMatchPlayers` (proyección cerrada,
   * sin datos personales). `clubPlayers` sustituye a `UserService.getPlayers(clubId)`
   * para el delegado; `matchPlayers` son los referenciados por el partido.
   */
  async getMatchPlayers(matchId: string): Promise<GetMatchPlayersResponse> {
    const fn = httpsCallable<GetMatchPlayersRequest, GetMatchPlayersResponse>(
      this.functions,
      'getMatchPlayers',
    );
    const { data } = await fn({ matchId });
    return data;
  }
}
